import { mergedSystems } from './system-merge.js';
import { overrideTypeFor } from './release-type-overrides.js';
import {
  RELEASE_TYPES, LISTED_TYPES, UNOFFICIAL_TYPES, NON_GAME_TYPES,
  DEFAULT_RELEASE_TYPES, FACET_LABELS, UNOFFICIAL_GROUP_LABEL,
  BADGE_LABELS, DETAIL_BADGE_LABELS, ROM_EXTENSIONS,
  RA_CATEGORY_LABELS, RA_PREFIXES,
} from './release-type-vocabulary.js';

export {
  RELEASE_TYPES, LISTED_TYPES, UNOFFICIAL_TYPES, NON_GAME_TYPES,
  DEFAULT_RELEASE_TYPES, FACET_LABELS, UNOFFICIAL_GROUP_LABEL,
  BADGE_LABELS, DETAIL_BADGE_LABELS, ROM_EXTENSIONS,
  RA_CATEGORY_LABELS, RA_PREFIXES,
};

const listedTypes = new Set(LISTED_TYPES);
const allTypes = new Set(RELEASE_TYPES);
const romExtensions = new Set(ROM_EXTENSIONS);
const SHARED_SPACE = /[ \t\n\r]/u;
const SHARED_SPACES = /[ \t\n\r]+/gu;
const HASH_RE = /^[0-9a-f]{8}$/u;

function asciiLower(value) {
  let out = '';
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    out += code >= 65 && code <= 90 ? String.fromCharCode(code + 32) : value[i];
  }
  return out;
}

function firstCodePoints(value, limit = 255) {
  let out = '';
  let count = 0;
  for (const ch of value) {
    if (count >= limit) break;
    out += ch;
    count += 1;
  }
  return { text: out, count, overflow: count === limit && value.length > out.length };
}

function trimShared(value) {
  let first = 0;
  let last = value.length;
  while (first < last && SHARED_SPACE.test(value[first])) first += 1;
  while (last > first && SHARED_SPACE.test(value[last - 1])) last -= 1;
  return value.slice(first, last);
}

export function codePointLength(value) {
  if (typeof value !== 'string') return 0;
  let count = 0;
  for (const _ of value) count += 1;
  return count;
}

export function isSubsetTitle(value) {
  return typeof value === 'string' && value.includes('[Subset - ') && value.endsWith(']');
}

export function parseRaTitle(raw) {
  if (typeof raw !== 'string') return { title: raw, gameType: 'official', unknownCategory: null };
  if (isSubsetTitle(raw)) return { title: raw, gameType: 'subset', unknownCategory: null };
  for (const { prefix, gameType, label } of RA_PREFIXES) {
    if (!raw.startsWith(prefix)) continue;
    const rest = raw.slice(prefix.length).replace(/^ */u, '');
    return { title: rest || label, gameType, unknownCategory: null };
  }
  if (raw.startsWith('~')) {
    const end = raw.indexOf('~', 1);
    if (end > 1) return { title: raw, gameType: 'official', unknownCategory: raw.slice(1, end) };
  }
  return { title: raw, gameType: 'official', unknownCategory: null };
}

function eachGroup(name, onGroup) {
  for (let i = 0; i < name.length; i += 1) {
    const open = name[i];
    if (open !== '(' && open !== '[') continue;
    const close = open === '(' ? ')' : ']';
    const end = name.indexOf(close, i + 1);
    if (end < 0) continue;
    onGroup(open, name.slice(i + 1, end));
    i = end;
  }
}

export function dumpSignals({ name, variant, isDirectory } = {}) {
  if (isDirectory) return { type: null, detail: null, badDump: false };
  const boundedName = typeof name === 'string' ? firstCodePoints(name).text : '';
  const boundedVariant = typeof variant === 'string' ? asciiLower(firstCodePoints(variant).text) : '';
  const found = new Set();
  let badDump = false;
  let beta = false;
  let translation = false;

  eachGroup(boundedName, (open, value) => {
    if (open === '(') {
      const token = asciiLower(value);
      if (token === 'hack') found.add('hack');
      if (token.startsWith('proto')) found.add('prototype');
      if (token.startsWith('beta')) { found.add('prototype'); beta = true; }
      if (token.startsWith('demo') || token.startsWith('sample') || token.startsWith('kiosk')) found.add('demo');
      if (token.startsWith('unl') || token.startsWith('pirate')) found.add('unlicensed');
      if (token === 'pd') found.add('homebrew');
    } else {
      if (/^b[0-9]*$/u.test(value)) badDump = true;
      if (/^p[0-9]*$/u.test(value)) found.add('unlicensed');
      if (value.startsWith('T+') || value.startsWith('T-') || value.startsWith('Trad-')) translation = true;
    }
  });
  if (boundedVariant.includes('prototype')) found.add('prototype');
  if (boundedVariant.includes('beta')) { found.add('prototype'); beta = true; }
  if (boundedVariant.includes('unlicensed')) found.add('unlicensed');
  if (boundedVariant.includes('public domain')) found.add('homebrew');
  if (boundedVariant.includes('english translation')) translation = true;

  const type = ['hack', 'prototype', 'demo', 'unlicensed', 'homebrew'].find((candidate) => found.has(candidate)) ?? null;
  return { type, detail: type === 'prototype' && beta ? 'beta' : type === null && translation ? 'translation' : null, badDump };
}

export function isRomName({ name, isDirectory = false, system, hasBundledProvenance = false } = {}) {
  if (typeof name !== 'string' || !name) return false;
  const length = firstCodePoints(name, 256);
  if (length.overflow || length.count > 255) return false;
  if (isDirectory) return system === 'c64';
  if (system !== 'c64' && (name.includes('/') || name.includes('\\'))) return false;
  const filename = name.slice(name.lastIndexOf('/') + 1);
  const dot = filename.lastIndexOf('.');
  if (dot < 0) return false;
  const finalExtension = asciiLower(filename.slice(dot + 1));
  if (romExtensions.has(finalExtension)) return true;
  if (finalExtension !== 'zip' && finalExtension !== '7z') return false;
  // Callers set provenance for this name only after checking the exact bundled
  // genesis/name/hash triple, including hash 8b34102c.
  if (name === 'CrazyBus v0.8.zip' && system === 'genesis') return Boolean(hasBundledProvenance);
  const stem = filename.slice(0, dot);
  const innerDot = stem.lastIndexOf('.');
  if (innerDot >= 0) {
    const inner = stem.slice(innerDot + 1);
    if (/^[A-Za-z0-9]{1,4}$/u.test(inner)) return romExtensions.has(asciiLower(inner));
  }
  return Boolean(hasBundledProvenance);
}

export function classifyItem({ gameType, signals, overrideType, isRom = true } = {}) {
  const signal = signals ?? { type: null, detail: null, badDump: false };
  const subsetLinked = gameType === 'subset';
  let type = 'official';
  if (gameType === 'invalid' || gameType === 'test_kit') type = gameType;
  else if (listedTypes.has(overrideType)) type = overrideType;
  else if (UNOFFICIAL_TYPES.includes(gameType)) type = gameType;
  else if (listedTypes.has(signal.type)) type = signal.type;
  const detail = type === 'prototype' && signal.detail === 'beta' ? 'beta'
    : type === 'official' && signal.detail === 'translation' ? 'translation' : null;
  const unlistedReason = !isRom ? 'not_rom'
    : gameType === 'invalid' ? 'invalid'
      : gameType === 'test_kit' ? 'test_kit' : null;
  return { listed: unlistedReason === null, unlistedReason, type, detail, subsetLinked };
}

export function releaseFieldsFor({ gameType, name, variant, isDirectory = false, system, hash } = {}) {
  if (name == null) return {
    release_type: allTypes.has(gameType) ? gameType : 'official',
    release_detail: null,
  };
  const signals = dumpSignals({ name, variant, isDirectory });
  const overrideType = typeof system === 'string' && typeof hash === 'string' && HASH_RE.test(hash)
    ? overrideTypeFor(system, hash) : null;
  const item = classifyItem({ gameType, signals, overrideType });
  return { release_type: item.type, release_detail: item.detail };
}

export function badgeLabelFor(value, detail) {
  const type = typeof value === 'object' && value !== null ? value.release_type : value;
  const releaseDetail = typeof value === 'object' && value !== null ? value.release_detail : detail;
  if (!allTypes.has(type)) return null;
  if (type === 'official' && releaseDetail === 'translation') return DETAIL_BADGE_LABELS.translation;
  if (type === 'prototype' && releaseDetail === 'beta') return DETAIL_BADGE_LABELS.beta;
  return BADGE_LABELS[type] ?? null;
}

export function usableHeaderTitle(raw) {
  if (typeof raw !== 'string') return null;
  const bounded = firstCodePoints(raw, 256);
  if (bounded.overflow || bounded.count > 255) return null;
  let useful = 0;
  for (const ch of bounded.text) {
    const cp = ch.codePointAt(0);
    if (cp <= 0x1f || (cp >= 0x7f && cp <= 0x9f)
      || cp === 0xfffd || cp === 0x061c || cp === 0xfeff
      || (cp >= 0x200b && cp <= 0x200f)
      || (cp >= 0x202a && cp <= 0x202e)
      || (cp >= 0x2060 && cp <= 0x2069)) return null;
    if ((cp >= 48 && cp <= 57) || (cp >= 65 && cp <= 90)
      || (cp >= 97 && cp <= 122) || cp > 127) useful += 1;
  }
  const title = trimShared(bounded.text);
  if (!title || useful < 2) return null;
  const first = title.codePointAt(0);
  if (((first >= 0x21 && first <= 0x2f) || (first >= 0x3a && first <= 0x40)
    || (first >= 0x5b && first <= 0x60) || (first >= 0x7b && first <= 0x7e))
    && first !== 0x22 && first !== 0x27 && first !== 0x28) return null;
  const lower = asciiLower(title);
  if (lower.endsWith('ltd.')) return null;
  if (lower.endsWith('ltd')) {
    const preceding = title.charCodeAt(title.length - 4);
    if (!((preceding >= 65 && preceding <= 90) || (preceding >= 97 && preceding <= 122))) return null;
  }
  return title;
}

function removeGroups(value) {
  let out = '';
  for (let i = 0; i < value.length; i += 1) {
    const open = value[i];
    if (open === '(' || open === '[') {
      const close = open === '(' ? ')' : ']';
      const end = value.indexOf(close, i + 1);
      if (end >= 0) { i = end; continue; }
    }
    out += open;
  }
  return out;
}

export function titleFromFilename(name, { isDirectory = false } = {}) {
  if (name == null) return null;
  if (typeof name !== 'string') return null;
  if (isDirectory) return name;
  const bounded = firstCodePoints(name).text;
  const lower = asciiLower(bounded);
  const archiveStripped = lower.endsWith('.zip') ? bounded.slice(0, -4)
    : lower.endsWith('.7z') ? bounded.slice(0, -3) : bounded;
  let result = trimShared(removeGroups(archiveStripped).replace(SHARED_SPACES, ' '));
  const dot = result.lastIndexOf('.');
  if (dot >= 0 && romExtensions.has(asciiLower(result.slice(dot + 1)))) {
    result = trimShared(result.slice(0, dot));
  }
  return result || archiveStripped;
}

function orderedTypes(values) {
  const selected = new Set(values);
  return LISTED_TYPES.filter((type) => selected.has(type));
}

export function parseReleaseTypesParam(value) {
  if (value === undefined) return { present: false, types: [] };
  let raw;
  if (typeof value === 'string') {
    if (value.length > 256) return { present: true, types: [] };
    raw = value;
  } else if (Array.isArray(value) && value.length <= 8 && value.every((part) => typeof part === 'string' && part.length <= 64)) {
    if (value.reduce((length, part) => length + part.length, 0) > 256) return { present: true, types: [] };
    raw = value.join(',');
  } else return { present: true, types: [] };
  return { present: true, types: orderedTypes(raw.split(',').map(trimShared).filter((type) => listedTypes.has(type))) };
}

export function serializeReleaseTypes(values) {
  return Array.isArray(values) ? orderedTypes(values.filter((type) => listedTypes.has(type))).join(',') : '';
}

export function sanitizeStoredReleaseTypes(value) {
  if (!Array.isArray(value)) return DEFAULT_RELEASE_TYPES;
  if (value.length === 0) return [];
  const types = orderedTypes(value.filter((type) => listedTypes.has(type)));
  return types.length ? types : DEFAULT_RELEASE_TYPES;
}

export function releaseFamily(system) {
  return typeof system === 'string' ? mergedSystems(system)[0] : null;
}
